import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './search-select.css';

export function SearchSelect({ id, value, onChange, options, placeholder = 'Select security', searchable = true, includeEmpty = true, label }: {
  id: string; value: string; onChange: (value: string) => void; options: { id: string; name: string }[]; placeholder?: string; searchable?: boolean; includeEmpty?: boolean; label?: string;
}) {
  const [open, setOpen] = useState(false); const [search, setSearch] = useState('');
  const root = useRef<HTMLDivElement>(null); const trigger = useRef<HTMLButtonElement>(null); const listId = useId();
  const menu = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !menu.current) return;
    const popup = menu.current;
    const position = () => {
      const rect = trigger.current!.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 340), window.innerWidth - 24);
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const height = Math.min(340, Math.max(below, above));
      Object.assign(popup.style, { width: `${width}px`, maxHeight: `${height}px`,
        left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
        top: below >= Math.min(340, above) ? `${rect.bottom + 4}px` : 'auto',
        bottom: below >= Math.min(340, above) ? 'auto' : `${window.innerHeight - rect.top + 4}px` });
    };
    position(); popup.showPopover();
    (popup.querySelector<HTMLInputElement>('input') ?? popup.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ?? popup.querySelector<HTMLButtonElement>('button'))?.focus();
    window.addEventListener('resize', position); window.addEventListener('scroll', position, true);
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    window.addEventListener('pointerdown', outside);
    return () => { window.removeEventListener('resize', position); window.removeEventListener('scroll', position, true); window.removeEventListener('pointerdown', outside); };
  }, [open]);
  const matching = options.filter(option => option.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
    };
    window.addEventListener('keydown', dismiss, true);
    return () => window.removeEventListener('keydown', dismiss, true);
  }, [open]);
  const choose = (next: string) => { onChange(next); setOpen(false); trigger.current?.focus(); };
  return <div className="search-select" ref={root} onBlur={event => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <button ref={trigger} id={id} type="button" className="search-select-trigger" aria-label={label} aria-expanded={open} aria-controls={listId} onClick={() => { setSearch(''); setOpen(!open); }}>
      <span>{options.find(option => option.id === value)?.name ?? placeholder}</span><ChevronDown size={14} />
    </button>
    {open && <div ref={menu} popover="manual" className="search-select-menu" id={listId} role="group" aria-label={label ? `${label} choices` : undefined}>
      {searchable && <input autoFocus aria-label="Search securities" placeholder="Search by ticker or name…" value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); if (matching[0]) choose(matching[0].id); } if (event.key === 'ArrowDown') { event.preventDefault(); root.current?.querySelector<HTMLButtonElement>('.search-select-options button')?.focus(); } }} />}
      <div className="search-select-options" onKeyDown={event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const buttons = Array.from(event.currentTarget.querySelectorAll('button')); const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        event.preventDefault(); buttons[event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
      }}>
        {includeEmpty && <button type="button" onClick={() => choose('')}>{placeholder}</button>}
        {matching.map(option => <button type="button" key={option.id} aria-pressed={value === option.id} onClick={() => choose(option.id)}>{option.name}</button>)}
        {!matching.length && <p role="status">No securities match your search.</p>}
      </div>
    </div>}
  </div>;
}
